import GameObject from '../../../GameObject/GameObject';
import TransformComponent from '../../../Component/Transform';
import AnimationClip from '../AnimationClip';
import Point from '../../../Utility/Point';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import BoomCard from '../../../Prototype/Level/Card/BoomCard';
import { Easing } from '@tweenjs/tween.js';
import Renderable from '../../../Component/Renderable';
import { Random } from '../../../Utility/Random';
import { delay } from '../../../Utility/Task/TaskQueue';
import {
    AwaitIterators,
    ConcatIterators,
    PlayAnimation,
    StartAnimation,
    StartAnimationClip,
} from '../AnimationHelper';
import CardSystem from '../../Card/CardSystem';
import { showCardScoreEffectAt } from '../CardAnimationHelper';
import { UIEvent } from '../../UI/BaseUISystem';
import DragonbonesComponent from 'Component/Dragonbones';
import { DragonbonesFadeIn } from '.';
import { MainAnim } from './MainAnim';
import { OverlayFadeOut } from './OverlayFadeOut';

interface ICardExplosionMeta {
    system: CardSystem;
    sourceCard: BoomCard;
    targetCards: BaseCard[];
    cards: BaseCard[];
}

export class CardExplosion extends AnimationClip {
    public *execute(
        animator: GameObject,
        meta: ICardExplosionMeta
    ): IterableIterator<number> {
        // Add to animator for layering
        meta.cards.forEach((card) => {
            animator.addChild(card);
            card.getComponent(Renderable).visible = true;
        });

        const cardsPositionAndScore: { position: Point, score: number }[] = meta.cards
            .map(card => ({
                position: card
                    .getComponent(TransformComponent)
                    .position.clone() as Point,
                score: card.score,
            }));

        const overlay = animator.getChildByName('overlay');
        StartAnimation(overlay, 'OverlayFadeIn');

        const durationIn = 750;
        const durationOut = 800;

        if (meta.sourceCard instanceof BoomCard) {
            const armature = meta.sourceCard.getComponentsInChildren(DragonbonesComponent)?.[0]?.gameObject;
            const cardFront = meta.sourceCard.findChildByName(BaseCard.CARD_FRONT_NAME);
            
            StartAnimationClip(new DragonbonesFadeIn(armature, {
                playTimes: 1,
                fadeInTime: 50,
            }));
            StartAnimationClip(new MainAnim(cardFront, {
                alpha: 0,
                duration: 50
            }));
        }

        // Bring cards in
        yield* this.playCardsIn(meta, durationIn, animator);

        // Push cards away
        yield* this.cardsTweenOut(meta, durationOut);

        // Show score at og position
        cardsPositionAndScore.forEach(card => {
            showCardScoreEffectAt(
                meta.system.scene,
                animator,
                card.score,
                card.position
            );
        });

        this.backgroundShake();

        yield* delay(durationOut);

        meta.sourceCard.getComponent(Renderable).visible = false;

        yield* PlayAnimation(new OverlayFadeOut(overlay));

        meta.system.scene.emit(UIEvent.RESTORE_INTERACTIVITY, true);
    }

    private backgroundShake() {
        const background = GameObject.findByName('backgroundSprite')[0];
        if (background) {
            StartAnimation(background, 'LevelScreenShake', {
                amplitude: 15,
                frequency: 25,
                damping: 0.9,
            });
        }
    }

    private *playCardsIn(
        meta: ICardExplosionMeta,
        durationIn: number,
        animator: GameObject
    ): IterableIterator<number> {
        const sourceCardTransform = meta.sourceCard.getComponent(
            TransformComponent
        );
        let iterators: IterableIterator<number>[] = [];
        const stageOneDuration = durationIn / 2;
        const stageTwoDuration = durationIn - stageOneDuration;
        const pulseRepetitions = 2;

        for (let card of meta.targetCards) {
            // Add as child to animator to bring in front of overlay.
            animator.addChild(card);

            const transform = card.getComponent(TransformComponent);
            const startPos = transform.position.clone();
            const translateTween = this.getTween(transform.position);
            const pulseTween = this.getTween(transform.position);
            const rotationTween = this.getTween(transform);

            const rotDirection = Random.int(0, 1) * 2 - 1;
            const dx = transform.x - sourceCardTransform.x;
            const dy = transform.y - sourceCardTransform.y;
            const motionVector = new Point(dx, dy);
            const normalVector = motionVector.clone();
            Point.normalize(normalVector);

            // Tween in.
            translateTween
                .to(
                    {
                        x: sourceCardTransform.x + motionVector.x * 0.25,
                        y: sourceCardTransform.y + motionVector.y * 0.25,
                    },
                    stageOneDuration
                )
                .easing(Easing.Quadratic.InOut);

            pulseTween
                .to(
                    {
                        x: sourceCardTransform.x + motionVector.x * 0.45,
                        y: sourceCardTransform.y + motionVector.y * 0.45,
                    },
                    stageTwoDuration / pulseRepetitions
                )
                .repeat(pulseRepetitions)
                .yoyo(true)
                .easing(Easing.Quadratic.InOut);

            rotationTween
                .to({ rotation: Math.PI * -0.5 * rotDirection }, stageOneDuration)
                .easing(Easing.Linear.None);

            iterators.push(ConcatIterators([this.startTween(translateTween), this.startTween(pulseTween)]), this.startTween(rotationTween))
        }

        // Add source card to front again
        animator.addChild(meta.sourceCard);

        yield* AwaitIterators(iterators);
    }

    private *cardsTweenOut(
        meta: ICardExplosionMeta,
        durationOut: number
    ): IterableIterator<number> {
        const sourceCardTransform = meta.sourceCard.getComponent(
            TransformComponent
        );

        for (let card of meta.targetCards) {
            const renderable = card.getComponent(Renderable);
            const transform = card.getComponent(TransformComponent);
            const translateTween = this.getTween(transform.position);
            const rotationTween = this.getTween(transform);

            const rotDirection = Random.int(0, 1) * 2 - 1;
            const dx = transform.x - sourceCardTransform.x;
            const dy = transform.y - sourceCardTransform.y;
            const motionVector = new Point(dx, dy);
            const normalVector = motionVector.clone();
            Point.normalize(normalVector);

            // Explode out.
            translateTween
                .to(
                    {
                        x: sourceCardTransform.x + normalVector.x * 1200,
                        y: sourceCardTransform.y + normalVector.y * 1000,
                    },
                    durationOut
                )
                .easing(Easing.Quadratic.Out)
                .start(this.elapsedTime);

            rotationTween
                .to({ rotation: Math.PI * 6 * -rotDirection }, durationOut)
                .easing(Easing.Quadratic.Out)
                .start(this.elapsedTime)
                .onComplete(() => (renderable.visible = false));
        }
    }
}
